import { cs } from '../../../lib/currency';
/**
 * Quick Enroll Dialog (iter270 — extracted from Enrollments.js).
 *
 * The biggest dialog in the file (~500L). Lets admin pick an existing
 * player, assign them to a program + one or more classes, set fee +
 * frequency, optionally apply a discount/override, and (rarely) edit
 * billing dates. All state + handlers owned by the parent.
 *
 * Props (15 total — biggest interface in the codebase, but each is
 * straightforward; nothing is duplicated state):
 *   - open                              (bool)
 *   - onOpenChange                      ((bool) => void)
 *   - quickEnrollData                   (object)
 *   - setQuickEnrollData                ((updater | object) => void)
 *   - filteredPlayers                   (Array<Player>)
 *   - parentStatusFilter                (string)
 *   - setParentStatusFilter             ((str) => void)
 *   - playerStatusFilter                (string)
 *   - setPlayerStatusFilter             ((str) => void)
 *   - playerSearch                      (string)
 *   - setPlayerSearch                   ((str) => void)
 *   - playerListSortDirection           ('asc' | 'desc')
 *   - setPlayerListSortDirection        ((updater) => void)
 *   - programs                          (Array<Program>)
 *   - discounts                         (Array<Discount>)
 *   - paymentFrequencies                (Array<Freq>) — PAYMENT_FREQUENCIES const
 *   - quickEnrollClasses                (Array<Class>) — filtered to selected program
 *   - quickEnrollLoading                (bool)
 *   - onProgramChange                   ((programId) => void)
 *   - onClassToggle                     ((classId) => void)
 *   - onFrequencyChange                 ((freq) => void)
 *   - onSubmit                          (() => Promise<void>)
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  User, Calendar, DollarSign, Check, ChevronUp, ChevronDown,
} from 'lucide-react';

export default function QuickEnrollDialog({
  open,
  onOpenChange,
  quickEnrollData,
  setQuickEnrollData,
  filteredPlayers,
  parentStatusFilter,
  setParentStatusFilter,
  playerStatusFilter,
  setPlayerStatusFilter,
  playerSearch,
  setPlayerSearch,
  playerListSortDirection,
  setPlayerListSortDirection,
  programs,
  discounts,
  paymentFrequencies,
  quickEnrollClasses,
  quickEnrollLoading,
  onProgramChange,
  onClassToggle,
  onFrequencyChange,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-green-500" />
            Quick Enroll Player
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Filter Options */}
          <div className="flex gap-3">
            <div className="flex-1 space-y-1">
              <Label className="text-xs text-slate-500">Parent Status</Label>
              <Select value={parentStatusFilter} onValueChange={setParentStatusFilter}>
                <SelectTrigger className="h-9" data-testid="parent-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active Parents</SelectItem>
                  <SelectItem value="inactive">Inactive Parents</SelectItem>
                  <SelectItem value="all">All Parents</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 space-y-1">
              <Label className="text-xs text-slate-500">Player Status</Label>
              <Select value={playerStatusFilter} onValueChange={setPlayerStatusFilter}>
                <SelectTrigger className="h-9" data-testid="player-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Players</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="lost_lead">Lost Lead</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Player Search */}
          <div className="space-y-2">
            <Label>Search Player</Label>
            <Input
              placeholder="Search by player or parent name..."
              value={playerSearch}
              onChange={(e) => setPlayerSearch(e.target.value)}
              data-testid="quick-enroll-player-search"
            />
          </div>

          {/* Player Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Select Player * <span className="text-xs text-slate-400 font-normal">({filteredPlayers.length} players)</span></Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setPlayerListSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
                className="h-7 px-2 text-xs text-slate-600 hover:text-slate-900"
                data-testid="sort-player-list"
              >
                Name {playerListSortDirection === 'asc' ? '(A-Z)' : '(Z-A)'}
                {playerListSortDirection === 'asc' ? (
                  <ChevronUp className="h-3 w-3 ml-1" />
                ) : (
                  <ChevronDown className="h-3 w-3 ml-1" />
                )}
              </Button>
            </div>
            <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1 bg-slate-50">
              {filteredPlayers.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-3">No players found</p>
              ) : (
                filteredPlayers.slice(0, 20).map(player => (
                  <label
                    key={player.id}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-white transition-colors ${
                      quickEnrollData.player_id === player.id ? 'bg-green-50 border border-green-200' : 'bg-white border border-transparent'
                    }`}
                  >
                    <input
                      type="radio"
                      name="player"
                      checked={quickEnrollData.player_id === player.id}
                      onChange={() => setQuickEnrollData(prev => ({ ...prev, player_id: player.id }))}
                      className="w-4 h-4 text-green-600 focus:ring-green-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{player.full_name || player.name}</p>
                        {player.status === 'lost_lead' && (
                          <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">Lost Lead</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        Parent: {player.parent_name || 'N/A'}
                        {player.skill_level && ` • ${player.skill_level}`}
                      </p>
                    </div>
                  </label>
                ))
              )}
              {filteredPlayers.length > 20 && (
                <p className="text-xs text-slate-400 text-center py-1">
                  Showing first 20 results. Refine your search.
                </p>
              )}
            </div>
          </div>

          {/* Program Selection */}
          <div className="space-y-2">
            <Label>Program *</Label>
            <Select value={quickEnrollData.program_id} onValueChange={onProgramChange}>
              <SelectTrigger data-testid="quick-enroll-program-select">
                <SelectValue placeholder="Select program" />
              </SelectTrigger>
              <SelectContent>
                {programs.filter(p => p.is_active !== false).map(program => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name} - {cs()}{program.price}/mo
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Class Selection */}
          {quickEnrollData.program_id && (
            <div className="space-y-2">
              <Label>Select Classes * <span className="text-slate-500 font-normal">(one or more)</span></Label>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1 bg-slate-50">
                {quickEnrollClasses.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-2">No classes available</p>
                ) : (
                  quickEnrollClasses.map(cls => (
                    <label
                      key={cls.id}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-white transition-colors ${
                        quickEnrollData.class_ids.includes(cls.id) ? 'bg-green-50 border border-green-200' : 'bg-white border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={quickEnrollData.class_ids.includes(cls.id)}
                        onChange={() => onClassToggle(cls.id)}
                        className="w-4 h-4 rounded text-green-600 focus:ring-green-500"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{cls.name}</p>
                        <p className="text-xs text-slate-500">
                          {cls.schedule_day} {cls.start_time}-{cls.end_time}
                          {cls.coach_name && ` • ${cls.coach_name}`}
                        </p>
                      </div>
                    </label>
                  ))
                )}
              </div>
              {quickEnrollData.class_ids.length > 0 && (
                <p className="text-xs text-green-600">{quickEnrollData.class_ids.length} class(es) selected</p>
              )}
            </div>
          )}

          {/* Payment Frequency */}
          <div className="space-y-2">
            <Label>Payment Plan</Label>
            <Select value={quickEnrollData.payment_frequency} onValueChange={onFrequencyChange}>
              <SelectTrigger data-testid="quick-enroll-frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {paymentFrequencies.map(f => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fee */}
          {quickEnrollData.program_id && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Monthly Rate:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={quickEnrollData.monthly_fee}
                    onChange={(e) => setQuickEnrollData(prev => ({ ...prev, monthly_fee: e.target.value }))}
                    className="w-24 h-8 text-right"
                    data-testid="quick-enroll-fee-input"
                  />
                  <span className="text-sm text-slate-500">/mo</span>
                </div>
              </div>
              <p className="text-xs text-green-700 mt-2">
                {quickEnrollData.prorate_first_month
                  ? '💡 Current month will be prorated. Recurring billing starts 1st of next month.'
                  : '⚠️ Full monthly fee will be charged for the start month (proration disabled).'}
              </p>
            </div>
          )}

          {/* Pro-rate first month — explicit admin toggle. Defaulted ON
              so mid-month enrollments don't quietly bill the full monthly
              fee. */}
          {quickEnrollData.program_id && (
            <label
              className="flex items-start gap-2 p-3 rounded-lg border bg-amber-50 border-amber-200 cursor-pointer"
              data-testid="quick-enroll-prorate-block"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 text-emerald-600 rounded"
                checked={!!quickEnrollData.prorate_first_month}
                onChange={(e) =>
                  setQuickEnrollData(prev => ({
                    ...prev,
                    prorate_first_month: e.target.checked,
                  }))
                }
                data-testid="quick-enroll-prorate-checkbox"
              />
              <span className="text-sm text-amber-900">
                <span className="font-medium">Pro-rate first month</span>
                <span className="block text-xs text-amber-700 mt-0.5">
                  When the start date is mid-month, charge only the remaining sessions
                  for this month. Recurring billing then resumes on the 1st.
                </span>
              </span>
            </label>
          )}

          {/* Discount + final-price override controls (iter231). */}
          {quickEnrollData.program_id && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-800">Discount &amp; price override</span>
                <span className="text-xs text-purple-500">(optional)</span>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Catalog discount</Label>
                <Select
                  value={quickEnrollData.discount_id || 'none'}
                  onValueChange={(value) => {
                    const v = value === 'none' ? '' : value;
                    setQuickEnrollData(prev => ({
                      ...prev,
                      discount_id: v,
                      discount_amount: v ? '' : prev.discount_amount,
                      discount_reason: v ? '' : prev.discount_reason,
                    }));
                  }}
                >
                  <SelectTrigger className="h-9" data-testid="quick-enroll-discount-select">
                    <SelectValue placeholder="Select a discount" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="z-[100]" sideOffset={4}>
                    <SelectItem value="none">No discount</SelectItem>
                    {(discounts || []).map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name} ({d.value ?? d.discount_value}{d.discount_type === 'percentage' ? '%' : '$'} off)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Custom discount ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={quickEnrollData.discount_amount}
                    disabled={!!quickEnrollData.discount_id}
                    onChange={(e) => setQuickEnrollData(prev => ({ ...prev, discount_amount: e.target.value }))}
                    placeholder="0.00"
                    className="h-9"
                    data-testid="quick-enroll-discount-amount"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Reason</Label>
                  <Input
                    value={quickEnrollData.discount_reason}
                    disabled={!!quickEnrollData.discount_id}
                    onChange={(e) => setQuickEnrollData(prev => ({ ...prev, discount_reason: e.target.value }))}
                    placeholder="e.g. Coach referral"
                    className="h-9"
                    data-testid="quick-enroll-discount-reason"
                  />
                </div>
              </div>

              <div className="space-y-1 pt-2 border-t border-purple-200">
                <Label className="text-xs text-slate-600">
                  Override final monthly fee ($)
                  <span className="ml-1 text-slate-400">— wins over the discount above</span>
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={quickEnrollData.final_monthly_fee}
                  onChange={(e) => setQuickEnrollData(prev => ({ ...prev, final_monthly_fee: e.target.value }))}
                  placeholder="Leave blank to use program price minus discount"
                  className="h-9"
                  data-testid="quick-enroll-final-fee"
                />
              </div>

              {/* Live "Final price will be:" preview (iter237/258). */}
              {(() => {
                const program = (programs || []).find(p => p.id === quickEnrollData.program_id);
                const freq = paymentFrequencies.find(f => f.value === quickEnrollData.payment_frequency)
                          || paymentFrequencies[0];
                const editedPerMonth = parseFloat(quickEnrollData.monthly_fee);
                const catalogPerMonth = parseFloat(program?.[freq.priceKey] || program?.price || 0) || 0;
                const perMonth = Number.isFinite(editedPerMonth) && editedPerMonth > 0
                  ? editedPerMonth : catalogPerMonth;
                const basePerCycle = perMonth * (freq.multiplier || 1);
                const overrideRaw = quickEnrollData.final_monthly_fee;
                const overrideNum = overrideRaw !== '' && overrideRaw !== null
                  ? parseFloat(overrideRaw)
                  : null;
                let computed = basePerCycle;
                let pathLabel = freq.value === 'monthly'
                  ? `Program price (${cs()}${basePerCycle.toFixed(2)}/mo)`
                  : `${cs()}${perMonth.toFixed(2)}/mo × ${freq.multiplier} = ${cs()}${basePerCycle.toFixed(2)}`;
                const selectedDiscount = (discounts || []).find(
                  d => d.id === quickEnrollData.discount_id
                );
                if (selectedDiscount) {
                  const dv = parseFloat(
                    selectedDiscount.value !== undefined && selectedDiscount.value !== null
                      ? selectedDiscount.value
                      : (selectedDiscount.discount_value || 0)
                  ) || 0;
                  const dollarOff = selectedDiscount.discount_type === 'percentage'
                    ? (basePerCycle * dv) / 100
                    : dv;
                  computed = Math.max(0, basePerCycle - dollarOff);
                  pathLabel = `${selectedDiscount.name} → −${cs()}${dollarOff.toFixed(2)}`;
                } else if (quickEnrollData.discount_amount && parseFloat(quickEnrollData.discount_amount) > 0) {
                  const dv = parseFloat(quickEnrollData.discount_amount);
                  computed = Math.max(0, basePerCycle - dv);
                  pathLabel = `Custom discount −${cs()}${dv.toFixed(2)}`;
                }
                if (overrideNum !== null && !Number.isNaN(overrideNum)) {
                  computed = Math.max(0, overrideNum);
                  pathLabel = `Override → ${cs()}${overrideNum.toFixed(2)} (wins)`;
                }
                const isZeroWithPaidProgram = computed <= 0 && basePerCycle > 0;
                const cycleSuffix = freq.value === 'monthly' ? 'month'
                                   : freq.value === 'quarterly' ? 'quarter'
                                   : freq.value === 'biannual' ? '6mo' : freq.value;
                return (
                  <div className="space-y-2 mt-2">
                    <div
                      className={`text-xs px-3 py-2 rounded-md border ${
                        isZeroWithPaidProgram
                          ? 'bg-amber-50 border-amber-300 text-amber-900'
                          : 'bg-emerald-50 border-emerald-300 text-emerald-900'
                      }`}
                      data-testid="quick-enroll-final-price-preview"
                    >
                      <div className="flex justify-between items-center">
                        <span>Final price will be:</span>
                        <span className="text-base font-semibold">
                          {cs()}{computed.toFixed(2)}
                          <span className="text-xs font-normal text-slate-500 ml-1">/{cycleSuffix}</span>
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {pathLabel}
                      </div>
                    </div>
                    {isZeroWithPaidProgram && (
                      <label className="flex items-start gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={quickEnrollData.confirm_zero_price}
                          onChange={(e) => setQuickEnrollData(prev => ({
                            ...prev,
                            confirm_zero_price: e.target.checked,
                          }))}
                          data-testid="quick-enroll-confirm-zero"
                        />
                        <span className="text-amber-900">
                          <span className="font-medium">I confirm $0/month is intentional</span>
                          <span className="block text-[11px] text-amber-700 mt-0.5">
                            Program base price is {cs()}{basePerCycle.toFixed(2)}. Tick this only
                            for scholarships / comps — otherwise check the override or
                            discount values above.
                          </span>
                        </span>
                      </label>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Advanced Billing Options (for data imports) */}
          {quickEnrollData.program_id && (
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Billing Dates</span>
                <span className="text-xs text-slate-400">(for data imports)</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Start Date</Label>
                  <Input
                    type="date"
                    value={quickEnrollData.start_date}
                    onChange={(e) => setQuickEnrollData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="h-9"
                    data-testid="quick-enroll-start-date"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Next Billing Date</Label>
                  <Input
                    type="date"
                    value={quickEnrollData.next_billing_date}
                    onChange={(e) => setQuickEnrollData(prev => ({ ...prev, next_billing_date: e.target.value }))}
                    className="h-9"
                    placeholder="Auto-calculated"
                    data-testid="quick-enroll-next-billing"
                  />
                  <p className="text-xs text-slate-400">Leave empty to auto-calculate</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            onClick={onSubmit}
            disabled={quickEnrollLoading || !quickEnrollData.player_id || quickEnrollData.class_ids.length === 0}
            data-testid="quick-enroll-submit-btn"
          >
            {quickEnrollLoading ? (
              <span className="animate-spin mr-1">⏳</span>
            ) : (
              <Check className="h-4 w-4 mr-1" />
            )}
            Enroll Player
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
